import React, { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { Bell, TrendingUp, TrendingDown } from 'lucide-react';

export const AlertsNotifier = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { formatCurrency } = useCurrency();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const getWebSocketUrl = useCallback(() => {
    if (!user?.id || !token) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backendUrl.replace(/^http/, 'ws');
    return `${wsUrl}/ws/alerts/${user.id}?token=${token}`;
  }, [user?.id, token]);

  const connect = useCallback(() => {
    const wsUrl = getWebSocketUrl();
    if (!wsUrl || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Alerts WebSocket connected');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          
          if (message.type === 'alerts_triggered' && message.alerts?.length > 0) {
            message.alerts.forEach(alert => {
              const isAbove = alert.alert_type === 'above';
              toast(
                <div className="flex items-start gap-3">
                  <div className={`p-2 ${isAbove ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                    {isAbove ? (
                      <TrendingUp size={18} className="text-emerald-400" />
                    ) : (
                      <TrendingDown size={18} className="text-red-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">Price Alert Triggered</p>
                    <p className="text-sm text-muted-foreground">
                      {alert.asset_name} is now {isAbove ? 'above' : 'below'} {formatCurrency(alert.target_value)}
                    </p>
                    <p className="text-xs text-gold mt-1">
                      Current: {formatCurrency(alert.current_value)}
                    </p>
                  </div>
                </div>,
                {
                  duration: 10000,
                  icon: <Bell size={18} className="text-gold" />,
                }
              );
            });
          }
        } catch (e) {
          console.error('Alerts message parse error:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('Alerts WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('Alerts WebSocket disconnected');
        // Reconnect after 10 seconds
        reconnectTimeoutRef.current = setTimeout(connect, 10000);
      };
    } catch (error) {
      console.error('Alerts WebSocket connection error:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getWebSocketUrl]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id && token) {
      connect();
    } else {
      disconnect();
    }

    return () => disconnect();
  }, [isAuthenticated, user?.id, token, connect, disconnect]);

  // This component doesn't render anything visible
  return null;
};
